// src/features/collaboration/titleSaveUtils.ts
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import * as Y from 'yjs';
import { generateSlug } from '../../utils/slug';
import { prepareTextForSearch } from './normalizationUtils';
import { generatePreview } from '../../utils/previewGenerator';
import { ASTNode } from '../../types/preview';
  import { doc as fsDoc, setDoc as fsSetDoc } from 'firebase/firestore';
import { assertWorkspaceInvariant } from '../../utils/workspaceInvariant';

interface TitleSaveOptions {
  workspaceId: string;
  boardId: string;
  userId: string;
  ydoc: Y.Doc;
  newTitle: string;
}

/**
 * タイトル変更時の専用保存関数
 * タイトル情報のみを保存（コンテンツ処理は除外してパフォーマンス向上）
 */
export const saveTitleWithPreviewAndSearchData = async (options: TitleSaveOptions): Promise<boolean> => {
  const { workspaceId, boardId, userId, ydoc, newTitle } = options;

  assertWorkspaceInvariant(workspaceId, { source: 'boards.saveTitleWithPreviewAndSearchData' });

  if (!newTitle.trim()) {
    console.warn('❌ タイトルが空のため保存をスキップ', {
      newTitle,
      timestamp: new Date().toISOString()
    });
    return false;
  }

  try {
    // 1. スラグ生成
    const slug = generateSlug(newTitle);

    // 2. タイトルの正規化処理
    const titleResult = prepareTextForSearch(newTitle);

    // 3. Firestoreへ保存（すべてのタイトル関連情報を同期）
    const boardRef = doc(db, 'workspaces', workspaceId, 'boards', boardId);

    const updateData: any = {
      // editData
      'editData.title': newTitle.trim(),
      
      // previewData
      'previewData.title': newTitle.trim(),
      'previewData.structuredData.headline': newTitle.trim(),
      'previewData.structuredData.dateModified': new Date().toISOString(),
      
      // searchData
      'searchData.originalTitle': titleResult.original,
      'searchData.normalizedTitle': titleResult.normalized,
      
      // 共通フィールド
      slug,
      lastModified: serverTimestamp(),
      lastModifiedBy: userId,

      // 通知の起点（専用フィールド）
      'notificationState.lastEditHeartbeatAt': serverTimestamp(),
      'notificationState.lastEditHeartbeatBy': userId,
      // 編集中ハートビート（通知の create デバウンス用）
      'notificationState.editingHeartbeatAt': serverTimestamp(),
      'notificationState.editingHeartbeatBy': userId
    };

    await updateDoc(boardRef, updateData);

    // 公開用JSONの外部保存（titleとslugAutoのみ）
    try {
      const publicRef = fsDoc(db, 'publicPages', workspaceId, 'pages', boardId);
      const slugAuto = generateSlug(newTitle.trim());
      await fsSetDoc(publicRef, { title: newTitle.trim(), slugAuto }, { merge: true });
    } catch (e) {
      console.warn('⚠️ title保存時のpublicPagesByWorkspace更新に失敗（処理継続）:', e);
    }

    // 軽量検証: 保存後にタイトルの整合性をチェック（非同期・ブロックしない）
    setTimeout(async () => {
      try {
        const verifyBoardRef = doc(db, 'workspaces', workspaceId, 'boards', boardId);
        const boardSnap = await getDoc(verifyBoardRef);
        
        if (boardSnap.exists()) {
          const data = boardSnap.data();
          const editDataTitle = data.editData?.title;
          const previewDataTitle = data.previewData?.title;
          const searchDataTitle = data.searchData?.originalTitle;
          
          // タイトルの不整合を検出
          const hasInconsistency = 
            (editDataTitle && previewDataTitle && editDataTitle !== previewDataTitle) ||
            (editDataTitle && searchDataTitle && editDataTitle !== searchDataTitle);
          
          if (hasInconsistency) {
            console.warn('⚠️ [Title Verification] タイトル不整合を検出', {
              boardId,
              editDataTitle,
              previewDataTitle,
              searchDataTitle,
              timestamp: new Date().toISOString(),
              説明: 'コンテンツ保存との競合が発生した可能性があります'
            });
            
            // 不整合を自動修復（previewDataとsearchDataをeditData.titleに合わせる）
            if (editDataTitle) {
              const titleResult = prepareTextForSearch(editDataTitle);
              
              await updateDoc(verifyBoardRef, {
                'previewData.title': editDataTitle,
                'previewData.structuredData.headline': editDataTitle,
                'searchData.originalTitle': titleResult.original,
                'searchData.normalizedTitle': titleResult.normalized,
                'notificationState.lastEditHeartbeatAt': serverTimestamp(),
                'notificationState.lastEditHeartbeatBy': userId,
                'notificationState.editingHeartbeatAt': serverTimestamp(),
                'notificationState.editingHeartbeatBy': userId
              });

            }
          } else {

          }
        }
      } catch (verifyError) {
        console.warn('⚠️ [Title Verification] 検証処理でエラーが発生（保存は成功しています）:', verifyError);
      }
    }, 1000); // 1秒後に検証（コンテンツ保存の完了を待つ）

    return true;
  } catch (error) {
    console.error('❌ タイトル専用保存エラー:', error);
    return false;
  }
};

/**
 * 既存のsaveTitleOnly関数の拡張版
 * 重複チェックとリンク更新も含む
 */
export const saveTitleOnlyWithAllData = async (
  newTitle: string,
  workspaceId: string,
  boardId: string,
  userId: string,
  ydoc: Y.Doc,
  generateUniqueTitle: (title: string) => Promise<string>,
  oldTitle: string,
  updateLinksCallback?: (oldTitle: string, newTitle: string) => Promise<void>
): Promise<boolean> => {
  if (!newTitle.trim()) {
    return false;
  }

  try {
    // 重複を避けたユニークタイトルを生成
    const uniqueTitle = await generateUniqueTitle(newTitle);

    // タイトル専用保存を実行
    const success = await saveTitleWithPreviewAndSearchData({
      workspaceId,
      boardId,
      userId,
      ydoc,
      newTitle: uniqueTitle
    });

    if (!success) {
      return false;
    }

    // プレビューデータも更新（エディタコンテンツが利用可能な場合）
    try {
      const editorContentJSON = (ydoc as any)._editorContentJSON;
      const savedTimestamp = (ydoc as any)._editorContentJSONTimestamp;

      // タイトル保存は30秒制限（ユーザー操作直後なので十分）
      const timeLimit = 30000;
      const isRecent = savedTimestamp && (Date.now() - savedTimestamp) < timeLimit;

      if (editorContentJSON && isRecent) {
        // contentJSONをASTNode配列として扱う
        let ast: ASTNode[] = [];
        if (editorContentJSON.type === 'doc' && Array.isArray(editorContentJSON.content)) {
          ast = editorContentJSON.content;
        } else if (Array.isArray(editorContentJSON)) {
          ast = editorContentJSON;
        }

        if (ast.length > 0) {
          const previewMeta = generatePreview(ast);

          // プレビューメタデータを更新（タイトル関連は既に更新済みなのでpreviewMetaのみ）
          const boardRef = doc(db, 'workspaces', workspaceId, 'boards', boardId);
          await updateDoc(boardRef, {
            'previewData.previewMeta': {
              ...previewMeta,
              generatedAt: serverTimestamp()
            }
          });
        } else {
          console.warn('   ⚠️ AST配列が空のため、previewMeta更新スキップ');
        }
      } else {
        if (editorContentJSON && !isRecent) {
          console.warn('   ⚠️ editorContentJSONが古すぎるため使用しない（経過:', Date.now() - savedTimestamp, 'ms）');
        } else {
          console.warn('   ⚠️ editorContentJSONが存在しないため、previewMeta更新スキップ');
        }
      }
    } catch (previewError) {
      console.warn('⚠️ タイトル保存時のプレビュー更新でエラーが発生:', previewError);
      console.error('   スタックトレース:', previewError);
      // プレビュー更新が失敗してもタイトル保存は成功とする
    }

    // リンク更新処理（オプション）
    if (updateLinksCallback && oldTitle && uniqueTitle !== oldTitle && !oldTitle.startsWith('Memo_')) {
      try {
        await updateLinksCallback(oldTitle, uniqueTitle);
      } catch (error) {
        console.error('❌ リンク元ページの更新エラー:', error);
        // リンク更新が失敗してもタイトル保存は成功とする
      }
    }

    return true;
  } catch (error) {
    console.error('❌ タイトル保存（全データ版）エラー:', error);
    return false;
  }
};

/**
 * タイトル変更後3秒遅延での軽量データ更新関数
 * searchData.title関連とpreviewData.titleのみを更新（コンテンツ処理なし）
 */
export const updateTitleRelatedDataOnly = async (
  newTitle: string,
  workspaceId: string,
  boardId: string,
  userId: string
): Promise<boolean> => {
  assertWorkspaceInvariant(workspaceId, { source: 'boards.updateTitleRelatedDataOnly' });
  if (!newTitle.trim()) {
    console.warn('❌ タイトルが空のため軽量更新をスキップ');
    return false;
  }

  try {
    // タイトルの正規化処理
    const titleResult = prepareTextForSearch(newTitle);

    // Firestoreへ軽量更新（タイトル関連フィールドのみ）
    const boardRef = doc(db, 'workspaces', workspaceId, 'boards', boardId);

    const updateData: any = {
      // previewDataのタイトル部分のみ更新
      'previewData.title': newTitle.trim(),
      'previewData.structuredData.headline': newTitle.trim(),
      'previewData.structuredData.dateModified': new Date().toISOString(),
      
      // searchDataのタイトル部分も更新
      'searchData.originalTitle': titleResult.original,
      'searchData.normalizedTitle': titleResult.normalized,
      
      lastModified: serverTimestamp(),
      lastModifiedBy: userId,
      'notificationState.lastEditHeartbeatAt': serverTimestamp(),
      'notificationState.lastEditHeartbeatBy': userId,
      'notificationState.editingHeartbeatAt': serverTimestamp(),
      'notificationState.editingHeartbeatBy': userId
    };

    await updateDoc(boardRef, updateData);

    return true;
  } catch (error) {
    console.error('❌ タイトル関連データ軽量更新エラー:', error);
    return false;
  }
};
